import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { SegmentControl } from '@/components/ui/segment-control';
import { BottomSheet } from '@/components/ui/dialog';
import { InvoicePay } from '@/components/InvoicePay';
import { DiscountCoverageHint } from '@/components/vouchers/DiscountCoverageHint';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';
import { api, ApiError } from '@/lib/api';
import { confirmAction, getWebApp, haptic } from '@/lib/telegram';
import { planTermLabel } from '@/lib/period';
import { toast } from 'sonner';
import { Ticket, Copy, Check, Loader2, Plus } from 'lucide-react';

export const Route = createFileRoute('/vouchers')({ component: Vouchers });

type Filter = 'available' | 'redeemed' | 'expired' | 'cancelled';

function Vouchers() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('available');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [newBatch, setNewBatch] = useState<string[] | null>(null);

  const { data: listData, isPending } = useQuery({
    queryKey: ['vouchers', filter],
    queryFn: () => api.vouchers(filter),
    placeholderData: keepPreviousData,
  });
  const { data: me } = useQuery({ queryKey: ['partner'], queryFn: () => api.me() });
  const items = listData?.items ?? [];
  // Партнёр раздаёт коды текстом, а список отдавал их по одному и вперемешку со
  // старыми пачками: после закрытия окна выпуска пачку было уже не собрать.
  const groups = useMemo(() => {
    const byBatch = new Map<string, typeof items>();
    for (const v of items) {
      const list = byBatch.get(v.batch_id) ?? [];
      list.push(v);
      byBatch.set(v.batch_id, list);
    }
    return [...byBatch.entries()].map(([batchId, list]) => ({
      batchId,
      items: list,
      planName: list[0].plan_name,
      createdAt: list[0].created_at,
    }));
  }, [items]);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    tg.MainButton.hide();
    const onBack = () => navigate({ to: '/marketing' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  return (
    <div className="p-4 space-y-4 pb-28">
      <div className="px-1 pt-2 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-display text-[22px] font-medium">Ваучеры</h2>
          {me && (
            <span className="text-[12.5px] font-medium bg-white/[0.06] text-muted-foreground px-3 py-1.5 rounded-full whitespace-nowrap nums">
              Доступно: {Math.round(me.spendableRub).toLocaleString('ru')} ₽
            </span>
          )}
        </div>
        {/* Разбивка нужна только тем, у кого депозит есть: остальным она добавила бы
            вторую цифру там, где и одной хватает. */}
        {me && me.paidRub > 0 && (
          <div className="text-[12px] text-dim nums -mt-1">
            депозит {Math.round(me.paidRub).toLocaleString('ru')} ₽ · заработано{' '}
            {Math.round(me.balanceRub).toLocaleString('ru')} ₽ (к выводу)
          </div>
        )}
        <p className="text-sm text-muted-foreground leading-relaxed">
          Купите пачку кодов с баланса и раздайте клиентам. Они вводят код в боте и получают бесплатную подписку.
          Код действует 30 дней.
        </p>
        <button
          type="button"
          onClick={() => {
            haptic('light');
            setSheetOpen(true);
          }}
          className="w-full bg-primary text-primary-foreground py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        >
          <Plus className="w-4 h-4" strokeWidth={2.2} />
          Создать пачку
        </button>
      </div>

      <SegmentControl<Filter>
        value={filter}
        onChange={setFilter}
        options={[
          { value: 'available', label: 'Доступные' },
          { value: 'redeemed', label: 'Погашенные' },
          { value: 'expired', label: 'Истекли' },
          { value: 'cancelled', label: 'Отменённые' },
        ]}
      />

      <div className="space-y-3">
        {isPending && !listData ? (
          <VouchersSkeleton />
        ) : items.length === 0 ? (
          <div className="surface-card rounded-[22px] py-10 px-5 text-center">
            <div className="w-[52px] h-[52px] rounded-[16px] bg-white/[0.05] flex items-center justify-center mx-auto mb-3.5 text-[#6b6d75]">
              <Ticket className="w-[26px] h-[26px]" strokeWidth={1.6} />
            </div>
            <h3 className="font-display font-semibold text-[15px] text-muted-foreground">Тут пусто</h3>
            <p className="text-[12.5px] text-dim mt-1.5">
              {filter === 'available' ? 'Нажмите «Создать пачку», чтобы купить первые ваучеры.' : 'Нет ваучеров в этом статусе.'}
            </p>
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.batchId} className="space-y-2">
              <div className="flex items-center justify-between gap-2 px-1">
                <div className="min-w-0">
                  <div className="text-[12px] font-semibold text-muted-foreground truncate">
                    {g.planName}
                  </div>
                  <div className="text-[12px] text-dim nums">
                    {new Date(g.createdAt).toLocaleDateString('ru')} · кодов: {g.items.length}
                  </div>
                </div>
                {filter === 'available' && g.items.length > 1 && (
                  <CopyBatchButton batchId={g.batchId} count={g.items.length} />
                )}
              </div>
              {g.items.map((v) => (
                <VoucherCard key={v.id} v={v} />
              ))}
            </div>
          ))
        )}
      </div>

      <BuyBatchSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onSuccess={(codes) => {
          setSheetOpen(false);
          setNewBatch(codes);
          qc.invalidateQueries({ queryKey: ['vouchers'] });
          qc.invalidateQueries({ queryKey: ['partner'] });
          qc.invalidateQueries({ queryKey: ['partner-me'] });
        }}
      />

      <BatchResultSheet
        codes={newBatch}
        onClose={() => setNewBatch(null)}
      />
    </div>
  );
}

/** «Скопировать коды» на пачке.
 *
 * Тянем пачку отдельным запросом, а не берём из списка: список ограничен страницей, и
 * копия молча оказалась бы неполной. Пачка не больше сотни кодов, так что это один
 * короткий запрос.
 */
function CopyBatchButton({ batchId, count }: { batchId: string; count: number }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const copy = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.vouchers('available', batchId);
      const codes = res.items.map((v) => v.code);
      if (codes.length === 0) {
        toast.error('Свободных кодов в пачке не осталось');
        return;
      }
      // Только коды, по одному в строке: этот текст уходит в чат как есть.
      await navigator.clipboard.writeText(codes.join('\n'));
      haptic('success');
      setDone(true);
      toast.success(`Скопировано кодов: ${codes.length}`);
      setTimeout(() => setDone(false), 1600);
    } catch {
      haptic('error');
      // Половина пачки хуже пустого буфера: партнёр раздаст часть и не заметит.
      toast.error('Не удалось собрать пачку, попробуйте ещё раз');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      disabled={busy}
      className="shrink-0 flex items-center gap-1.5 text-[11.5px] font-semibold text-primary-light px-2.5 py-1.5 rounded-full bg-white/[0.07] active:scale-95 transition-transform disabled:opacity-50"
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : done ? (
        <Check className="w-3.5 h-3.5" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
      {done ? 'Скопировано' : `Скопировать ${count}`}
    </button>
  );
}

function VoucherCard({ v }: { v: NonNullable<Awaited<ReturnType<typeof api.vouchers>>>['items'][0] }) {
  const [copied, setCopied] = useState(false);
  const qc = useQueryClient();

  const { mutate: cancel, isPending: cancelling } = useMutation({
    mutationFn: () => api.cancelVoucher(v.id),
    onSuccess: (res) => {
      haptic('success');
      // У скидочного кода денег не списывали, возвращать нечего: «0 ₽ вернулись»
      // выглядело как сбой. Показываем сумму только когда она есть.
      const refund = Math.round(res.refund_display_minor / 100);
      toast.success(refund > 0 ? `Код отменён, ${refund} ₽ вернулись на баланс` : 'Код отменён');
      qc.invalidateQueries({ queryKey: ['vouchers'] });
      qc.invalidateQueries({ queryKey: ['partner'] });
      // Дашборд читает ['partner-me'] — отдельная запись кэша. Без неё цифра на
      // главной остаётся старой, и возврат снова выглядит как «деньги не вернули».
      qc.invalidateQueries({ queryKey: ['partner-me'] });
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не удалось отменить код');
    },
  });

  const onCancel = async () => {
    if (cancelling) return;
    // Отмена необратима, поэтому спрашиваем. Кода у клиента может уже не быть на
    // руках, но погашенный он отменить не даст — это проверяет бэк. У скидочного кода
    // денег не списывали, и «деньги вернутся» было бы неправдой.
    // У мультикода часть активаций уже могла быть погашена: вернётся то, что ещё
    // заморожено под код, а не полная цена.
    const refund =
      (v.max_activations ?? 1) > 1 && v.held_display_minor != null
        ? `${Math.round(v.held_display_minor / 100)} ${v.display_currency === 'RUB' ? '₽' : v.display_currency}`
        : cost;
    const ok = await confirmAction(
      v.kind === 'discount'
        ? 'Код перестанет работать. Денег за него не списывали, возвращать нечего.'
        : `Код перестанет работать, ${refund} вернутся на баланс.`,
      { title: `Отменить код ${v.code}?`, okText: 'Да, отменить' },
    );
    if (ok) cancel();
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(v.code);
      setCopied(true);
      haptic('success');
      toast.success('Код скопирован');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };
  const cost = `${Math.round(v.cost_display_minor / 100)} ${v.display_currency === 'RUB' ? '₽' : v.display_currency}`;
  const statusBadge =
    v.status === 'available' ? { cls: 'bg-white/[0.06] text-foreground', label: 'Активен' } :
    v.status === 'redeemed' ? { cls: 'bg-white/[0.04] text-dim', label: 'Погашен' } :
    v.status === 'cancelled' ? { cls: 'bg-white/[0.07] text-muted-foreground', label: 'Отменён' } :
    { cls: 'bg-[rgba(248,113,113,0.14)] text-danger', label: 'Истёк' };

  return (
    <div className="surface-card rounded-[18px] p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={v.status === 'available' ? onCopy : undefined}
            className={`flex items-center gap-2 font-mono text-base font-semibold ${
              v.status === 'available' ? 'text-foreground active:scale-[0.97]' : 'text-dim line-through'
            }`}
            disabled={v.status !== 'available'}
          >
            {v.code}
            {v.status === 'available' && (copied ? <Check className="w-4 h-4 text-money-light" /> : <Copy className="w-4 h-4 text-primary-light" />)}
          </button>
          <span className={`text-[12px] font-medium px-2.5 py-1 rounded-full ${statusBadge.cls}`}>{statusBadge.label}</span>
        </div>
        <div className="text-xs text-muted-foreground flex justify-between">
          <span>{v.plan_name}</span>
          {/* У скидочного кода цены нет: расход возникает при покупке клиентом. */}
          <span className="nums">{v.kind === 'discount' ? `−${v.discount_percent ?? 0}%` : cost}</span>
        </div>
        {/* Мультикод: партнёру важнее «сколько ещё сработает», чем сам факт выпуска. */}
        {(v.max_activations ?? 1) > 1 && (
          <div className="flex items-center gap-2">
            <div className="h-1.5 flex-1 rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full rounded-full bg-white/50"
                style={{
                  width: `${Math.min(100, ((v.used_count ?? 0) / (v.max_activations ?? 1)) * 100)}%`,
                }}
              />
            </div>
            <span className="text-[12px] text-dim nums shrink-0">
              {v.used_count ?? 0} из {v.max_activations}
              {(v.held_display_minor ?? 0) > 0 &&
                ` · ${Math.round((v.held_display_minor ?? 0) / 100)} ${
                  v.display_currency === 'RUB' ? '₽' : v.display_currency
                } заморожено`}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="text-[12px] text-dim">
            {v.status === 'available' && `Истекает ${new Date(v.expires_at).toLocaleDateString('ru')}`}
            {v.status === 'redeemed' && v.redeemed_at && `Погашен ${new Date(v.redeemed_at).toLocaleDateString('ru')}`}
            {v.status === 'expired' && `Истёк ${new Date(v.expires_at).toLocaleDateString('ru')}`}
            {v.status === 'cancelled' &&
              (v.cost_display_minor > 0 ? 'Отменён, деньги вернулись на баланс' : 'Отменён')}
          </div>
          {v.status === 'available' && (
            <button
              type="button"
              onClick={onCancel}
              disabled={cancelling}
              className="text-[12px] font-semibold text-danger px-2.5 py-1 rounded-lg bg-[rgba(248,113,113,0.1)] active:scale-[0.97] transition-transform disabled:opacity-50 shrink-0"
            >
              {cancelling ? 'Отменяю…' : 'Отменить'}
            </button>
          )}
        </div>
    </div>
  );
}

function BuyBatchSheet({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (codes: string[]) => void;
}) {
  const qc = useQueryClient();
  // Два вида кода живут в одной форме: у бесплатного деньги списываются вперёд,
  // у скидочного — при покупке клиентом, из маржи партнёра.
  const [mode, setMode] = useState<'free' | 'discount'>('free');
  const [planUuid, setPlanUuid] = useState<string | null>(null);
  const [count, setCount] = useState(1);
  const [customName, setCustomName] = useState('');
  const [percent, setPercent] = useState('20');
  // Скидочный код бывает пачкой разных кодов или одним кодом на много активаций.
  // Второе партнёры просили прямым текстом: раздать «hex» на 30 человек проще,
  // чем рассылать тридцать разных строк.
  const [shape, setShape] = useState<'many' | 'single'>('many');
  const [customCode, setCustomCode] = useState('');
  // Один код на много активаций доступен обоим видам. У бесплатного каждая активация
  // это замороженные деньги, поэтому пачка оплачивается сразу целиком.
  const singleCode = shape === 'single';
  // Бесплатные ваучеры стоят денег, и взять их можно двумя путями. Скидочные ничего
  // не стоят при выпуске, поэтому выбора там нет.
  const [payWith, setPayWith] = useState<'balance' | 'invoice'>('balance');
  const byInvoice = mode === 'free' && payWith === 'invoice';

  const { data: plansData } = useQuery({
    queryKey: ['voucher-plans'],
    queryFn: () => api.voucherPlans(),
    enabled: open,
  });
  const { data: me } = useQuery({
    queryKey: ['partner'],
    queryFn: () => api.me(),
    enabled: open,
  });
  const { data: providersData, isError: providersFailed } = useQuery({
    queryKey: ['partner-payment-providers'],
    queryFn: () => api.partnerPaymentProviders(),
    enabled: open,
  });
  const plans = plansData?.plans ?? [];
  const selected = plans.find((p) => p.plan_uuid === planUuid) ?? null;
  // У «одного кода на всех» count это активации, а не число кодов: платим за все,
  // потому что каждая активация выдаст настоящую подписку.
  const total = selected ? Math.round((selected.cost_per_voucher_display_minor * count) / 100) : 0;
  const currency = selected?.display_currency === 'RUB' ? '₽' : selected?.display_currency ?? '';
  // Бэк списывает оба кармана (`debit_partner_drafts`: сначала внесённое счётом,
  // потом заработанное), поэтому и проверять надо по их сумме. Пока сверяли только с
  // заработанным, партнёр с деньгами на депозите видел «не хватает» и шёл платить
  // второй раз.
  const balance = Math.round(me?.spendableRub ?? 0);
  const paid = Math.round(me?.paidRub ?? 0);
  const insufficient = !!selected && total > balance;

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      if (mode === 'discount') {
        return singleCode
          ? api.issueDiscountVouchers(1, Number(percent) || 0, {
              activations: count,
              customCode: customCode.trim() || null,
            })
          : api.issueDiscountVouchers(count, Number(percent) || 0);
      }
      if (!planUuid) throw new Error('no plan');
      return singleCode
        ? api.purchaseVouchers(planUuid, 1, customName, count)
        : api.purchaseVouchers(planUuid, count, customName);
    },
    onSuccess: (data) => {
      haptic('success');
      toast.success(
        singleCode && mode === 'free'
          ? `Код создан: ${count} подписок`
          : `Создано ${data.codes.length} ваучеров`,
      );
      onSuccess(data.codes);
      setPlanUuid(null);
      setCount(1);
      setCustomName('');
      setCustomCode('');
    },
    onError: (e: Error) => {
      haptic('error');
      // ApiError несёт HTTP-статус: 402=insufficient_balance, 400=plan_inactive,
      // 409=not_available. Сверяем по статусу, а не по тексту (бэк может менять
      // формулировки error_message без warning).
      if (e instanceof ApiError) {
        if (e.status === 402) {
          toast.error('Недостаточно средств на балансе');
          return;
        }
        if (e.status === 400) {
          toast.error(e.message || 'Этот тариф больше недоступен. Выберите другой.');
          return;
        }
        if (e.status === 409) {
          toast.error(e.message || 'Такой код уже занят. Придумайте другой.');
          return;
        }
      }
      toast.error(e.message || 'Не удалось купить ваучеры');
    },
  });

  return (
    <BottomSheet open={open} onClose={onClose} title="Создать пачку ваучеров">
      <div className="grid grid-cols-2 gap-[9px]">
        {([
          { key: 'free', label: 'Бесплатная подписка' },
          { key: 'discount', label: 'Скидка %' },
        ] as const).map((opt) => (
          <button
            type="button"
            key={opt.key}
            onClick={() => {
              haptic('selection');
              setMode(opt.key);
            }}
            className={`rounded-[14px] px-3 py-2.5 text-[13px] font-semibold transition-colors ${
              mode === opt.key
                ? 'bg-white/[0.08] border border-white/45 text-foreground'
                : 'surface-inset text-muted-foreground'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {mode === 'free' ? (
        <p className="text-sm text-muted-foreground leading-relaxed">
          Выберите тариф и количество. Сумма спишется с баланса, вы получите коды.
          <br />
          <span className="text-amber">Возврат денег невозможен. 30 дней на раздачу.</span>
        </p>
      ) : (
        <p className="text-sm text-muted-foreground leading-relaxed">
          Скидка действует на следующую покупку клиента. Оплачиваете её вы: сначала из
          наценки по этой покупке, а то, что глубже наценки, с вашего баланса. Сейчас
          списывать нечего: сумма известна только когда клиент выберет тариф.
        </p>
      )}

      {mode === 'discount' && (
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground">
            Скидка, %
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={percent}
            onChange={(e) => setPercent(e.target.value.replace(/[^0-9.]/g, ''))}
            className="w-full surface-inset rounded-[14px] px-4 py-3 text-sm outline-none focus:border-white/30"
          />
          <DiscountCoverageHint percent={Number(percent) || 0} />
        </div>
      )}

      <div className="space-y-2">
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground">
            Как раздавать
          </label>
          <div className="grid grid-cols-2 gap-[9px]">
            {([
              { key: 'many', label: 'Разные коды' },
              { key: 'single', label: 'Один на всех' },
            ] as const).map((opt) => (
              <button
                type="button"
                key={opt.key}
                onClick={() => {
                  haptic('selection');
                  setShape(opt.key);
                }}
                className={`rounded-[14px] px-3 py-2.5 text-[13px] font-semibold transition-colors ${
                  shape === opt.key
                    ? 'bg-white/[0.08] border border-white/45 text-foreground'
                    : 'surface-inset text-muted-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-[12px] text-dim px-1 leading-snug">
            {singleCode
              ? mode === 'free'
                ? 'Один код на несколько подписок. Один клиент активирует его только раз, деньги за все активации замораживаются сразу.'
                : 'Один код, который сработает заданное число раз. Один клиент активирует его только раз.'
              : 'Каждому клиенту свой код. Так видно, кто именно активировал.'}
          </p>
        </div>
      </div>

      {/* Свой код принимает только выпуск скидочных: `purchaseVouchers` такого
          параметра не имеет, и поле у бесплатных молча выбрасывало бы ввод. */}
      {singleCode && mode === 'discount' && (
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground">
            Код <span className="text-dim font-normal">· необязательно</span>
          </label>
          <input
            type="text"
            maxLength={16}
            value={customCode}
            onChange={(e) => setCustomCode(e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))}
            placeholder="например, HEX"
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm font-mono focus:outline-none focus:border-white/30"
          />
          <p className="text-[12px] text-dim px-1">
            Латиница, цифры, дефис. Пусто: придумаем сами. Регистр не важен.
          </p>
        </div>
      )}

      <div className={mode === 'free' ? 'space-y-2' : 'hidden'}>
        <label className="text-xs font-semibold text-muted-foreground">Тариф</label>
        <div className="space-y-2">
          {plans.map((p) => {
            const cost = `${Math.round(p.cost_per_voucher_display_minor / 100)} ${p.display_currency === 'RUB' ? '₽' : p.display_currency}`;
            return (
              <button
                key={p.plan_uuid}
                type="button"
                onClick={() => setPlanUuid(p.plan_uuid)}
                className={`w-full text-left p-3.5 rounded-[15px] border transition-colors duration-100 flex items-center justify-between ${
                  planUuid === p.plan_uuid ? 'border-white/30 bg-white/[0.07]' : 'surface-inset'
                }`}
              >
                <div>
                  <div className="font-semibold text-sm">{p.name}</div>
                  <div className="text-[12px] text-dim mt-0.5">
                    {[`${p.devices} устр.`, planTermLabel(p, 'дн.')].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div className="font-display font-semibold text-sm nums">{cost}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-semibold text-muted-foreground">
          {singleCode ? 'Активаций' : 'Количество'}
        </label>
        <div className="flex items-center gap-2">
          {[1, 5, 10, 25].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setCount(n)}
              className={`flex-1 px-3 py-2.5 rounded-[12px] text-sm font-semibold transition-colors ${
                count === n ? 'bg-white/[0.12] text-foreground' : 'surface-inset text-muted-foreground'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <input
          type="number"
          min={1}
          max={singleCode && mode === 'discount' ? 1000 : 100}
          value={count}
          onChange={(e) => {
            // Скидочный код ничего не стоит при выпуске, бесплатный замораживает
            // деньги под каждую активацию — отсюда разные потолки.
            const max = singleCode && mode === 'discount' ? 1000 : 100;
            setCount(Math.max(1, Math.min(max, Number(e.target.value) || 1)));
          }}
          placeholder="или своё число"
          className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none focus:border-white/30"
        />
      </div>

      {/* Название пачки есть только у бесплатных: у скидочного кода клиент видит
          процент, а не тариф. */}
      {mode === 'free' && (
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground">
            Название <span className="text-dim font-normal">· необязательно</span>
          </label>
          <input
            type="text"
            maxLength={128}
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder={selected?.name || 'как у тарифа'}
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none focus:border-white/30"
          />
          <p className="text-[12px] text-dim px-1">Что клиент увидит при активации. Пусто: название тарифа.</p>
        </div>
      )}

      {mode === 'free' && (
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground">
            Откуда платим
          </label>
          <div className="grid grid-cols-2 gap-[9px]">
            {([
              { key: 'balance', label: 'С баланса' },
              { key: 'invoice', label: 'Оплатить счётом' },
            ] as const).map((opt) => (
              <button
                type="button"
                key={opt.key}
                onClick={() => {
                  haptic('selection');
                  setPayWith(opt.key);
                }}
                className={`rounded-[14px] px-3 py-2.5 text-[13px] font-semibold transition-colors ${
                  payWith === opt.key
                    ? 'bg-white/[0.08] border border-white/45 text-foreground'
                    : 'surface-inset text-muted-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {byInvoice && (
            <p className="text-[11.5px] text-dim px-1 leading-snug">
              Оплаченные деньги уходят прямо на эти ваучеры. Вывести их нельзя, только
              потратить на ваучеры и призы.
            </p>
          )}
        </div>
      )}

      <div className={mode === 'free' && !byInvoice ? 'space-y-1 px-1' : 'hidden'}>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Доступно</span>
          <span className={`font-display font-semibold nums ${insufficient ? 'text-danger' : 'text-foreground'}`}>
            {balance} {currency || '₽'}
          </span>
        </div>
        {paid > 0 && (
          <div className="text-[12px] text-dim nums text-right">
            депозит {paid} · заработано {balance - paid} (к выводу)
          </div>
        )}
      </div>
      {insufficient && !byInvoice && (
        <p className="text-xs text-danger px-1">
          Не хватает {total - balance} {currency}. Оплатите счётом или уменьшите количество.
        </p>
      )}

      {byInvoice ? (
        <InvoicePay
          providers={providersData?.providers}
          providersFailed={providersFailed}
          disabled={!selected}
          label={selected ? `Оплатить ${total} ${currency}` : 'Выберите тариф'}
          buildBody={(provider) => ({
            purpose: 'voucher_batch' as const,
            provider,
            plan_uuid: planUuid ?? undefined,
            // «Один код на всех»: счёт на одну строку, но на все её активации. Без
            // этого партнёр оплатил бы одну подписку, а получил код на несколько.
            count: singleCode ? 1 : count,
            activations: singleCode ? count : 1,
            custom_name: customName || null,
          })}
          onPaid={() => {
            qc.invalidateQueries({ queryKey: ['vouchers'] });
            qc.invalidateQueries({ queryKey: ['partner'] });
            onClose();
          }}
        />
      ) : (
      <button
        type="button"
        disabled={
          isPending ||
          (mode === 'free'
            ? !selected || insufficient
            : !(Number(percent) > 0 && Number(percent) <= 100))
        }
        onClick={() => mutate()}
        className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
      >
        {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        {mode === 'discount'
          ? singleCode
            ? `Выпустить код на ${count} активаций по −${Number(percent) || 0}%`
            : `Выпустить ${count} шт. по −${Number(percent) || 0}%`
          : !selected
            ? 'Выберите тариф'
            : insufficient
              ? 'Недостаточно баланса'
              : `Купить за ${total} ${currency}`}
      </button>
      )}
    </BottomSheet>
  );
}

function BatchResultSheet({ codes, onClose }: { codes: string[] | null; onClose: () => void }) {
  const copyAll = async () => {
    if (!codes) return;
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      haptic('success');
      toast.success('Все коды скопированы');
    } catch {
      toast.error('Не удалось скопировать');
    }
  };
  return (
    <BottomSheet open={codes !== null} onClose={onClose} title="Ваучеры созданы">
      <p className="text-sm text-muted-foreground leading-relaxed">
        Скопируйте коды и раздайте клиентам. Они вводят их в боте в меню «Промокод».
      </p>
      <div className="surface-inset rounded-[13px] p-3.5 max-h-[40dvh] overflow-y-auto overscroll-contain">
        <div className="font-mono text-sm space-y-1">
          {(codes ?? []).map((c) => (
            <div key={c}>{c}</div>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={copyAll}
        className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
      >
        <Copy className="w-4 h-4" />
        Скопировать все
      </button>
    </BottomSheet>
  );
}

/** Карточка ваучера: код с бейджем статуса, тариф с ценой, срок действия. */
function VouchersSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <SkeletonCard key={i} className="rounded-[18px] min-h-0 p-4 space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <SkeletonLine size="title" className="w-32" />
            <Skeleton className="h-[22px] w-20 rounded-full shrink-0" />
          </div>
          <div className="flex justify-between">
            <SkeletonLine size="sm" className="w-1/3" />
            <SkeletonLine size="sm" className="w-14" />
          </div>
          <SkeletonLine size="xs" className="w-2/5" />
        </SkeletonCard>
      ))}
    </>
  );
}
